import { Common } from '../Common';
import { Configuration } from './Configuration';
import { WindowActiveConfiguration } from './WindowActiveConfiguration';
import { WindowSmallConfiguration } from './WindowSmallConfiguration';
import { PriceSegment, DiscountGroups, PricePart } from '@icc/price/b2b';
import { TimeLimitSegment } from '@icc/common/time-limit/time-limit-functions';
import { core } from '../helpers';
import {
    Fitting,
    HandlesType,
    Hinge,
    Layout,
    Lock,
    ProfileSet,
    Shape,
    System,
    WindowParameters,
    SideProfileV2,
    SashesType,
    Measurement,
    FrameV2,
    HandleV2,
    SashV2,
    MullionV2,
    AlignmentV2,
    CouplingV2,
    MuntinsConfigV2,
    LockV2,
    FittingV2,
    SystemV2,
    ProfileV2,
    ProfileShapeV2,
    SashTypeV2,
    ReinforcementV2,
    GlazingBeadProfile,
    DoorHardwareV2,
    DoorSizes,
    SystemPacket,
    DoorSize,
    DoorLightsSize,
    Casing
} from '@icc/window';

import { MosquitoConfiguration } from './MosquitoConfiguration';
import { RollerShutter } from './parts/roller_shutter';

import {
    Accessory,
} from './parts/common';

import type {
    ColorType,
    GlazingSpacer,
    Wood,
    Filling,
    GlazingSpacerV2,
    FillingV2,
} from './parts/common';
import {
    changesInVersion4,
    changesInVersion5,
    changesInVersion6,
    changesInVersion8,
    changesInVersion9,
    changesInVersion10,
    muntinsConversion,
    changesInVersion7,
    changesInVersion11,
    changesInVersion13,
} from './converters/window';
import { ColorsV2, ColorId, ColorV2 } from './parts/common/Colors';
import { HingeV2 } from 'libs/window/src/lib/types/Hinge';
import { WoodV2 } from './parts/common/Wood';
import { ActiveMullion } from '../layout/active-mullion';
import { ActiveSash } from '../layout/active-sash';
import { ConfiguratorsDataService } from '../configurators/configurators-data.service';
import { IccSashType, HardwareVisualization, DoorPortal } from '../data-types';
import { iccColorToColorId } from './converters/window/colors';

export class WindowConfiguration implements Configuration {
    //#region pola interfejsu
    /**
     * Wersja interfejsu
     */
    $version = 13;
    /**
     * Typ
     */
    type: 'window' | 'door' | 'hs' | 'folding_door' | 'sliding_door' = 'window';

    /**
     * Nazwa konstrukcji
     */
    name = '';
    /**
     * Cena netto konstrukcji
     */
    price: number | null = null;
    /**
     * Cena netto konstrukcji bez marży
     */
    priceNoMargin: number | null = null;
    /**
     * Cena netto konstrukcji po rabatach
     */
    priceAfterDiscounts: number | null = null;
    /** Cena brutto konstrukcji */
    priceGross: number | null = null;
    /** Cena brutto konstrukcji po rabatach */
    priceGrossAfterDiscounts: number | null = null;
    /**
     * Liczba sztuk
     */
    quantity = 1;
    priceSegments: PriceSegment[] = [];
    priceSegmentsNoMargin: PriceSegment[] = [];
    priceParts: PricePart[] = [];
    pricePartsNoMargin: PricePart[] = [];
    discountGroups: DiscountGroups = {};
    discountGroupsNoMargin: DiscountGroups = {};
    timeLimit: number | null = null;
    timeLimitsStack: TimeLimitSegment[] = [];
    title = '';
    description = '';
    attachments: any[] = [];
    deletedAttachments: any[] = [];
    /**
     * Poprawność składowych konstrukcji
     */
    valid: {
        system: null | boolean;
        shape: null | boolean;
        dimensions: null | boolean;
        sashes: null | boolean;
        sealColor: null | boolean;
        siliconeColor: null | boolean;
        colors: null | boolean;
        wood: null | boolean;
        profileSet: null | boolean;
        loadedMuntinsColors: null | boolean;
        loadedProfiles: null | boolean;
        mullionsProfiles: null | boolean;
        sashesProfiles: null | boolean;
        frameProfiles: null | boolean;
        loadedGlazingBeads: null | boolean;
        loadedFillings: null | boolean;
        glazingBeads: null | boolean;
        fillings: null | boolean;
        extensionsColors: null | boolean;
        extensionsSystems: null | boolean;
        muntins: null | boolean;
        muntinsColors: null | boolean;
        loadedThresholdColor: null | boolean;
        thresholdColor: null | boolean;
        loadedLippingColor: null | boolean;
        lippingColor: null | boolean;
        loadedFillingsColors: boolean;
        innerLippingColor: null | boolean;
        casing: null | boolean;
    } = {
        system: null,
        shape: null,
        dimensions: null,
        sashes: null,
        sealColor: null,
        siliconeColor: null,
        colors: null,
        wood: null,
        profileSet: null,
        loadedMuntinsColors: null,
        loadedFillingsColors: false,
        loadedProfiles: null,
        mullionsProfiles: null,
        sashesProfiles: null,
        frameProfiles: null,
        loadedGlazingBeads: null,
        loadedFillings: null,
        glazingBeads: null,
        fillings: null,
        extensionsColors: null,
        extensionsSystems: null,
        muntins: null,
        muntinsColors: null,
        loadedThresholdColor: null,
        thresholdColor: null,
        loadedLippingColor: null,
        lippingColor: null,
        innerLippingColor: null,
        casing: null,
    };

    /**
     * Słownik z danymi składowych konfiguracji
     */
    dictionary: {
        /**
         * Kolory
         */
        colors: Record<string, ColorV2>;
        /**
         * Kolory list przyszybowych
         */
        glazingBeadColors: Record<ColorV2['id'], ColorV2>;
        /**
         * Kolory uszczelek
         */
        sealColors: Record<ColorV2['id'], ColorV2>;
        /**
         * Kolory silikonów
         */
        siliconeColors: Record<ColorV2['id'], ColorV2>;
        /**
         * Kolory dodatków (klamek, zawiasów)
         */
        accessoryColors: Record<ColorV2['id'], ColorV2>;
        /**
         * Rodzaj drewna
         */
        woods: Record<WoodV2['id'], WoodV2>;
        /**
         * Ciepła ramka
         */
        glazingSpacers: Record<GlazingSpacerV2['id'], GlazingSpacerV2>;
        /**
         * Zawias
         */
        hinges: Record<HingeV2['id'], HingeV2>;
        /**
         * Zamek
         */
        locks: Record<LockV2['id'], LockV2>;
        /**
         * Zestaw okuciowy
         */
        fittings: Record<FittingV2['id'], FittingV2>;
        /**
         * Linia produktowa
         */
        systems: Record<SystemV2['id'], SystemV2>;
        /**
         * Profile
         */
        profiles: Record<ProfileV2['id'], ProfileV2>;
        /**
         * Kształty profili
         */
        profileShapes: Record<ProfileShapeV2['id'], ProfileShapeV2>;
        /**
         * Funkcje skrzydeł (kierunki otwierania)
         */
        sashTypes: Record<SashTypeV2['id'], SashTypeV2>;
        /**
         * Wzmocnienia
         */
        reinforcements: Record<ReinforcementV2['id'], ReinforcementV2>;
        /**
         * Klamki
         */
        handles: Record<HandleV2['id'], HandleV2>;
        /**
         * Wypełnienia (pakiety szybowe, panele)
         */
        fillings: Record<FillingV2['id'], FillingV2>;
        /**
         * Osprzęt drzwi
         */
        doorHardware: Record<DoorHardwareV2['id'], DoorHardwareV2>;
        /**
         * Systemy akcesoriów
         */
        hardwareSystems: Record<DoorHardwareV2['id'], DoorHardwareV2>;
        /**
         * Rozmiary drzwi
         */
        doorSizes: Record<DoorSize['id'], DoorSize>;
        /**
         * Rozmiary doświetli drzwi
         */
        doorLightSizes: Record<DoorLightsSize['id'], DoorLightsSize>;
    } = {
        colors: {},
        glazingBeadColors: {},
        sealColors: {},
        siliconeColors: {},
        accessoryColors: {},
        woods: {},
        glazingSpacers: {},
        hinges: {},
        locks: {},
        fittings: {},
        systems: {},
        profiles: {},
        profileShapes: {},
        sashTypes: {},
        reinforcements: {},
        handles: {},
        fillings: {},
        doorHardware: {},
        hardwareSystems: {},
        doorSizes: {},
        doorLightSizes: {},
    };

    isMistakeProduct = false;
    hasWarranty = true;
    /**
     * Pomiary
     */
    measurements: Measurement[] = [];
    //#endregion

    //#region kształt, rozmiar, współczynniki
    /**
     * Wysokość konstrukcji
     */
    height = 1500;
    /**
     * Szerokość konstrukcji
     */
    width = 1500;
    /**
     * Kształt konstrukcji
     */
    shape: Shape = {
        shape: 'rect',
        width: 1500,
        height: 1500,
        circuit: 6000,
        s1: 1500,
        s2: 0,
        s3: 0,
        h1: 1500,
        h2: 0,
        h3: 1500,
    };
    /**
     * Rozmiary drzwi
     */
    doorSizes: DoorSizes = {
        sashSize: 0,
        sashSizeId: null,
        passiveSashSize: 0,
        passiveSashSizeId: null,
        shortening: 0,
        frameShortening: 0,
        topLightSize: 0,
        topLightSizeId: null,
        leftLightSize: 0,
        leftLightSizeId: null,
        rightLightSize: 0,
        rightLightSizeId: null,
        customSize: false,
        customWidth: 0,
        customHeight: 0,
    };

    /**
     * Parametry konstrukcji
     */
    parameters: WindowParameters = {
        weight: null,
    };
    //#endregion

    //#region kolory, drewno, wykończenie
    /**
     * Kolorystyka konstrukcji
     */
    colors: ColorsV2 = {
        frame: {
            outer: null,
            inner: null,
            core: null,
            alushell: null,
        },
        sash: {
            outer: null,
            inner: null,
            core: null,
            alushell: null,
        },
    };
    /**
     * Zestaw kolorów w konstrukcji
     */
    colorType: ColorType = 'White';
    /**
     * Rozszerzony zestaw kolorów konstrukcji
     */
    colorsSashExt = false;
    /**
     * Id rodzaju drewna
     */
    wood: Wood['id'] | null = null;
    /**
     * Sposób obróbki nakładki alumiowej
     */
    alushellType: 'painted' | 'brushed' | null = null;
    /**
     * Id koloru listwy przyszybowej
     */
    glazingBeadColor: ColorId = null;
    /**
     * Id koloru uszczelki
     */
    sealColor: ColorId = null;
    /**
     * Id koloru silikonu
     */
    siliconeColor: ColorId = null;
    /**
     * Kolory progu
     */
    thresholdColor: ColorId = null;
    /**
     * Kolor listwy mocującej
     */
    lippingColor: ColorId = null;
    innerLippingColor: ColorId = null;
    //#endregion

    //#region wypełnienia
    /**
     * Id typu ciepłej ramki
     */
    glazingBeadType: boolean | number = false;
    /**
     * Id ciepłej ramki
     */
    glazingSpacer: GlazingSpacer['id'] = null;
    /**
     * Pakiety szybowe zdefiniowane przez użytkownika
     */
    customFillings: Filling[] = [];
    /**
     * Czy pakiet szybowy jest objęty gwarancją.
     */
    hasGlazingWarranty = true;
    //#endregion

    //#region akcesoria
    /**
     * Dodatki do konstrukcji
     */
    accessories: Accessory[] = [];
    /**
     * Dodatki do krawędzi konstrukcji
     */
    sideAccessories: {
        /**
         * Dodatki do górnej krawędzi konstrukcji
         */
        top: Accessory[];
        /**
         * Dodatki do dolnej krawędzi konstrukcji
         */
        bottom: Accessory[];
        /**
         * Dodatki do lewej krawędzi konstrukcji
         */
        left: Accessory[];
        /**
         * Dodatki do prawej krawędzi konstrukcji
         */
        right: Accessory[];
    } = {
        top: [],
        bottom: [],
        left: [],
        right: [],
    };
    /**
     * Portal drzwiowy
     */
    doorPortal: DoorPortal | null = null;
    //#endregion

    //#region okucia
    /**
     * Czy kostrukcja ma klamkę/pochwyt.
     */
    hasHandle = false;
    /**
     * Typ pochwytu/klamki
     */
    handleType: HandlesType = 'InnerLever';
    /**
     * Id zestawu zawiasowego
     */
    hinge: {
        id: Hinge['id'];
        color?: ColorId;
        visualizations: HardwareVisualization[] | null;
    } = null;
    /**
     * Id zamku
     */
    lock: Lock['id'] = null;
    /**
     * Id zestawu okuciowego
     */
    fitting: Fitting['id'] = null;
    //#endregion

    //#region profile, wzmocnienia, system
    /**
     * Id linii produktowej
     */
    system: System['id'] | null = null;

    /**
     * Pakiety systemów
     */
    systemPackets: SystemPacket['id'][] = [];

    /** Stal otwarta/zamknięta */
    steel: 'Closed' | 'Opened' | null = null;
    /**
     * Czy konstrukcja z nakładką aluminiową.
     */
    hasAlushell = false;
    /**
     * Zestaw domyślnych profili
     */
    profileSet: ProfileSet = {
        id: null,
        name: '',
        frameTop: null,
        frameBottom: null,
        frameSide: null,
        sash: null,
        fixedMullion: null,
        falseMullion: null,
        threshold: null,
        sashOutward: null,
        centralHandleFalseMullion: null,
        doubleHandleFalseMullion: null,
        zMullion: null,
        glazingBeadShape: null,
        systems: [],
    };
    /**
     * Kawałki profili
     */
    usedProfilesSegments: {
        [profileId: number]: number[];
    } = {};

    /**
     * Wykońćzenie zgrzewu
     */
    weldFinishType: 'V' | 'thin' = 'V';
    /**
     * Położenie odpływu
     */
    drainagePosition: 'bottom' | 'front' = 'front';

    /**
     * Id niskiego progu (jeśli zastosowano)
     */
    lowThreshold: false | number = false;
    //#endregion

    //#region konstrukcja
    /**
     * Ramy
     */
    frames: FrameV2[] = [];
    /**
     * Kwatery
     */
    sashes: SashV2[] = [];
    /**
     * Słupki/poprzeczki
     */
    mullions: MullionV2[];
    /**
     * Wyrównania
     */
    alignments: AlignmentV2[];
    /**
     * Łączniki
     */
    couplings: CouplingV2[] = [];
    /**
     * Profile boczne (poszerzenia, listwy przyparapetowe, itp.)
     */
    sideProfiles: SideProfileV2[] = [];

    rollerShutter: RollerShutter = null;
    mosquito: MosquitoConfiguration = null;

    sashesType: SashesType = 'Fix';
    ownedSashesTypes = {
        window: true,
        doorActive: false,
        doorPassive: false,
        doorTopLight: false,
        doorLeftLight: false,
        doorRightLight: false,
    };

    /**
     * Czy konstrukcja balkonowa
     */
    balcony = false;
    // kicker = false;
    /**
     * Został ustawiony profil podwaliny
     */
    foundationProfile = false;
    /**
     * Układ konstrukcji
     */
    layout: Layout | null = null;
    /**
     * Opcje szprosów
     */
    muntins: MuntinsConfigV2 = null;
    //#endregion

    //#region one
    oneGlazing = false;
    oneFilling: {
        window: string | false;
        doorActive: string | false;
        doorActiveInner: string | false;
        doorPassive: string | false;
        doorLight: string | false;
        doorTopLight: string | false;
        doorLeftLight: string | false;
        doorRightLight: string | false;
    } = {
        window: false,
        doorActive: false,
        doorActiveInner: false,
        doorPassive: false,
        doorLight: false,
        doorTopLight: false,
        doorLeftLight: false,
        doorRightLight: false,
    };
    oneGlazingBead = false;
    oneGlazingBeadSash: {
        fix: boolean | number;
        sashes: boolean | number;
    } = {
        fix: false,
        sashes: false,
    };
    oneHandle = false;
    oneHandlesHeight = false;
    oneReinforcement = true;
    oneBondedGlazing = false;

    attributes: {
        id: string;
        name: string;
        value: string;
        valueId?: number;
    }[] = [];
    //#endregion

    casing: Casing = {
        id: null,
        name: null,
        code: null,
        innerWidth: null,
        wallOverlap: null,
        isCasingSelected: null
    };

    static converters = {
        4: changesInVersion4,
        5: changesInVersion5,
        6: changesInVersion6,
        7: changesInVersion7,
        8: changesInVersion8,
        9: changesInVersion9,
        10: changesInVersion10,
        11: changesInVersion11,
        13: changesInVersion13,
    };

    constructor(
        configuration?,
        dataRequiredToUpdate?: any,
        conversion = true,
        protected configuratorsDataService?: ConfiguratorsDataService,
        protected colorsDefaultsService?,
        private langCode = configuration?.langCode || 'pl'
    ) {
        if (WindowConfiguration.is(configuration) && conversion) {
            this.runConversion(
                configuration,
                Object.assign(dataRequiredToUpdate, {
                    langCode: this.langCode,
                })
            );
        } else {
            if (WindowActiveConfiguration.is(configuration)) {
                this.name = configuration.Name;
                this.price = configuration.Price;
                this.timeLimit = configuration.timeLimit;
                this.timeLimitsStack = configuration.timeLimitsStack;
                this.priceNoMargin = configuration.PriceNoMargin;
                this.priceAfterDiscounts = configuration.PriceAfterDiscounts || configuration.Price;
                this.priceGross = configuration.PriceGross || configuration.Price;
                this.priceGrossAfterDiscounts =
                    configuration.PriceGrossAfterDiscounts || configuration.Price;
                this.quantity = configuration.Quantity;
                this.priceSegments = configuration.PriceSegments;
                this.priceSegmentsNoMargin = configuration.PriceSegmentsNoMargin;
                this.priceParts = configuration.PriceParts;
                this.pricePartsNoMargin = configuration.PricePartsNoMargin;
                this.discountGroups = configuration.DiscountGroups;
                this.discountGroupsNoMargin = configuration.DiscountGroupsNoMargin;
                this.title = configuration.Title;
                this.description = configuration.Description;
                this.attachments = configuration.Attachments;
                this.valid = configuration.valid;
                this.attributes = configuration.attributes;
                this.casing = configuration.Casing;

                this.height = configuration.Height;
                this.width = configuration.Width;
                this.dictionary.systems[Number(configuration.System.id)] = {
                    id: Number(configuration.System.id),
                    imageSideOnOffer: configuration.System.image_side_on_offer,
                    name: configuration.System.name,
                    titleImage: configuration.System.title_image,
                    type: configuration.System.type,
                    offerInfo: configuration.System.offer_info,
                    frameType: configuration.System.type_frame,
                    confType: configuration.System.confType,
                    doorType: configuration.System.door_type,
                    frameJointAngles: configuration.System.frame_joint_angles,
                    sashJointAngles: configuration.System.sash_joint_angles,
                    thresholdJointAngles: configuration.System.threshold_joint_angles,
                    glazingBeadJointAngles: configuration.System.glazing_bead_joint_angles,
                    availableThresholdSashRebate: configuration.System.available_threshold_sash_rebate,
                    indexNo: configuration.System.index_no,
                    windowLineGroupId: configuration.System.window_line_group_id,
                    seperateFrameAndSashShortening: configuration.System.seperate_frame_and_sash_shortening
                };
                this.system = Number(configuration.System.id);
                this.systemPackets = configuration.systemPackets;

                (['frame', 'sash'] as const).forEach(part => {
                    (['outer', 'inner', 'core', 'alushell'] as const).forEach(side => {
                        if (configuration.Colors[part][side]?.id) {
                            this.dictionary.colors[
                                Number(configuration.Colors[part][side].id)
                                    + '+'
                                    + Number(configuration.Colors[part][side].RAL)
                            ] = {
                                id: Number(configuration.Colors[part][side].id),
                                name: {
                                    [this.langCode]: configuration.Colors[part][side].name,
                                },
                                code: configuration.Colors[part][side].code,
                                color: configuration.Colors[part][side].color,
                                color_img: configuration.Colors[part][side].color_img,
                                RAL: Boolean(configuration.Colors[part][side].RAL),
                                type: configuration.Colors[part][side].type,
                                groups:
                                    (configuration.Colors[part][side].groups
                                        && configuration.Colors[part][side].groups.map(Number))
                                    || [],
                                visualization: configuration.Colors[part][side].visualization
                            };
                            this.colors[part][side] = {
                                id: Number(configuration.Colors[part][side].id),
                                RAL: Boolean(configuration.Colors[part][side].RAL),
                                isDefault: configuration.Colors[part][side].isDefault,
                            };
                        }
                    });
                });

                this.colorType = configuration.ColorType as ColorType;
                this.colorsSashExt = configuration.ColorsSashExt;
                this.steel = configuration.Steel;
                if (configuration.Wood && configuration.Wood.id) {
                    this.dictionary.woods[Number(configuration.Wood.id)] = {
                        id: Number(configuration.Wood.id),
                        name: {
                            [this.langCode]: configuration.Wood.name,
                        },
                    };
                    this.wood = Number(configuration.Wood.id);
                }
                this.hasAlushell = configuration.HasAlushell;
                if (this.hasAlushell) {
                    this.alushellType = configuration.AlushellType;
                }
                this.shape = {
                    shape: configuration.Shape.shape,
                    circuit: configuration.Shape.circuit,
                };
                (['width', 'height', 's1', 's2', 's3', 'h1', 'h2', 'h3', 'd'] as const).forEach(
                    field => {
                        if (configuration.Shape[field] != null) {
                            this.shape[field] = Number(configuration.Shape[field]);
                        }
                    }
                );
                if (configuration.Shape.type != null) {
                    this.shape.type = configuration.Shape.type;
                }
                if (configuration.Shape.arcType != null) {
                    this.shape.arcType = configuration.Shape.arcType;
                }
                this.sashes = configuration.Sashes.map<SashV2>(sash =>
                    this.mapSash(sash, sash, configuration)
                );
                this.sashesType = configuration.SashesType;
                this.ownedSashesTypes = {
                    window: configuration.OwnedSashesTypes.window,
                    doorActive: configuration.OwnedSashesTypes.doorActive,
                    doorPassive: configuration.OwnedSashesTypes.doorPassive,
                    doorTopLight: configuration.OwnedSashesTypes.doorTopLight,
                    doorLeftLight: configuration.OwnedSashesTypes.doorLeftLight,
                    doorRightLight: configuration.OwnedSashesTypes.doorRightLight,
                };
                this.sideProfiles = configuration.SideProfiles.map(c => {
                    if (c.reinforcement) {
                        this.dictionary.reinforcements[Number(c.reinforcement.id)] = {
                            id: Number(c.reinforcement.id),
                            name: {
                                [this.langCode]: c.reinforcement.name,
                            },
                            price: c.reinforcement.price,
                        };
                    }
                    const dimensions = {
                        width: 0,
                        height: 0,
                    };
                    if (configuration.drawData) {
                        const extensionDimensions = configuration.drawData.extension.find(
                            el => el.extensionId === c.id
                        );
                        if (extensionDimensions) {
                            dimensions.width = extensionDimensions.rect.width;
                            dimensions.height = extensionDimensions.rect.height;
                        }
                    }
                    return {
                        ...c,
                        reinforcement: (c.reinforcement && c.reinforcement.id) || null,
                        dimensions,
                    };
                });
                this.alignments = configuration.Alignments;
                this.mullions = configuration.Mullions.map<MullionV2>(this.mapMullion.bind(this));
                this.dictionary.profiles = configuration.UsedProfiles.map(profile => ({
                    ...profile,
                    name: {
                        [this.langCode]: profile.name,
                    },
                })).reduce<Record<string, ProfileV2>>((prev, cur) => {
                    prev[cur.id] = cur;
                    return prev;
                }, {});
                this.dictionary.profileShapes = configuration.UsedProfileShapes.map(
                    profileShape => ({
                        ...profileShape,
                        name: {
                            [this.langCode]: profileShape.name,
                        },
                    })
                ).reduce<Record<string, ProfileShapeV2>>((prev, cur) => {
                    prev[cur.id] = cur;
                    return prev;
                }, {});
                this.usedProfilesSegments = configuration.UsedProfilesSegments;
                this.customFillings = configuration.CustomFillings;
                this.profileSet = configuration.ProfileSet;
                this.balcony = configuration.Balcony;
                this.foundationProfile = configuration.foundationProfile;

                if (configuration.Layout) {
                    this.layout = {
                        id: Number(configuration.Layout.id),
                        name: configuration.Layout.name,
                        conf: 'window',
                        changed:
                            configuration.Layout.divs && configuration.Layout.sashes
                                ? configuration.Layout.changed
                                : true,
                        sashes: configuration.Layout.sashes,
                        divs: configuration.Layout.divs,
                        sashTypes: configuration.Layout.sashTypes,
                        fromCode: false,
                        layoutCode: null,
                        countSashes: 0,
                        shape: 0,
                        transomsOnSeperateFrames: false,
                    };
                }
                this.oneGlazing = configuration.OneGlazing;
                this.oneFilling = {
                    window: configuration.OneFilling.window,
                    doorActive: configuration.OneFilling.doorActive,
                    doorActiveInner: configuration.OneFilling.doorActiveInner,
                    doorPassive: configuration.OneFilling.doorPassive,
                    doorLight: configuration.OneFilling.doorLight,
                    doorTopLight: configuration.OneFilling.doorTopLight,
                    doorLeftLight: configuration.OneFilling.doorLeftLight,
                    doorRightLight: configuration.OneFilling.doorRightLight,
                };
                this.oneGlazingBead = configuration.OneGlazingBead;
                this.oneHandle = configuration.OneHandle;
                this.oneHandlesHeight = configuration.OneHandlesHeight;
                this.oneReinforcement = configuration.OneReinforcement;
                if (configuration.GlazingBeadColor && configuration.GlazingBeadColor.id) {
                    this.dictionary.glazingBeadColors[Number(configuration.GlazingBeadColor.id)] = {
                        id: Number(configuration.GlazingBeadColor.id),
                        name: {
                            [this.langCode]: configuration.GlazingBeadColor.name,
                        },
                        RAL: Boolean(configuration.GlazingBeadColor.RAL),
                    };
                    this.glazingBeadColor = {
                        id: Number(configuration.GlazingBeadColor.id),
                        isDefault: configuration.GlazingBeadColor.isDefault,
                        RAL: Boolean(configuration.GlazingBeadColor.RAL),
                    };
                }
                if (configuration.SealColor && configuration.SealColor.id) {
                    this.dictionary.sealColors[Number(configuration.SealColor.id)] = {
                        id: Number(configuration.SealColor.id),
                        name: {
                            [this.langCode]: configuration.SealColor.name,
                        },
                    };
                    this.sealColor = {
                        id: Number(configuration.SealColor.id),
                        isDefault: configuration.SealColor.isDefault,
                    };
                }
                if (configuration.SiliconeColor && configuration.SiliconeColor.id) {
                    this.dictionary.siliconeColors[Number(configuration.SiliconeColor.id)] = {
                        id: Number(configuration.SiliconeColor.id),
                        name: {
                            [this.langCode]: configuration.SiliconeColor.name,
                        },
                    };
                    this.siliconeColor = {
                        id: Number(configuration.SiliconeColor.id),
                        isDefault: configuration.SiliconeColor.isDefault,
                    };
                }
                this.hasHandle = configuration.HasHandle;
                this.handleType = configuration.HandleType as HandlesType;
                if (configuration.Hinge && configuration.Hinge.id) {
                    this.dictionary.hinges[Number(configuration.Hinge.id)] = {
                        id: Number(configuration.Hinge.id),
                        name: {
                            [this.langCode]: configuration.Hinge.name,
                        },
                        priceSource: configuration.Hinge.price_source,
                    };
                    if (configuration.HingeColor?.id) {
                    this.dictionary.accessoryColors[Number(configuration.HingeColor.id)] = {
                        id: Number(configuration.HingeColor.id),
                        name: {
                            [this.langCode]: configuration.HingeColor.name,
                        },
                        color: configuration.HingeColor.color,
                        code: configuration.HingeColor.code,
                        color_img: configuration.HingeColor.color_img,
                    };
                        this.hinge = {
                            id: Number(configuration.Hinge.id),
                            color: {
                                id: Number(configuration.HingeColor.id),
                                isDefault: configuration.HingeColor.isDefault,
                            },
                            visualizations:
                                  configuration.Hinge.visualizations
                                  && ((configuration.HingeColor
                                      && configuration.Hinge.visualizations[configuration.HingeColor.id])
                                      || configuration.Hinge.visualizations[-1]),
                        };
                    } else {
                        this.hinge = {
                            id: Number(configuration.Hinge.id),
                            visualizations:
                                  configuration.Hinge.visualizations && configuration.Hinge.visualizations[-1]
                        };
                    }
                  
                }
                if (configuration.Lock && configuration.Lock.id) {
                    this.dictionary.locks[Number(configuration.Lock.id)] = {
                        id: Number(configuration.Lock.id),
                        name: {
                            [this.langCode]: configuration.Lock.name,
                        },
                        indexNo: configuration.Lock.index_no
                    };
                    this.lock = Number(configuration.Lock.id);
                }
                if (configuration.HasWarmEdge && configuration.WarmEdge) {
                    this.dictionary.glazingSpacers[Number(configuration.WarmEdge.id)] = {
                        id: Number(configuration.WarmEdge.id),
                        name: {
                            [this.langCode]: configuration.WarmEdge.name,
                        },
                    };
                    this.glazingSpacer = Number(configuration.WarmEdge.id);
                }
                if (
                    this.sashes.some(
                        sash =>
                            (sash.muntins && !!sash.muntins.length)
                            || sash.intSashes.some(
                                intSash => intSash.muntins && !!intSash.muntins.length
                            )
                    )
                ) {
                    this.muntins = {
                        sizeId: Number(configuration.MuntinsData.sizeId),
                        size: Number(configuration.MuntinsData.rWidth),
                        colorDraw: configuration.MuntinsData.colorDraw,
                        colorInner: null,
                        colorOuter: null,
                        type: {
                            id: Number(configuration.MuntinsData.typeCode),
                            type: configuration.MuntinsData.type,
                        },
                        duplex: configuration.MuntinsData.duplex,
                    };
                    if (configuration.MuntinsData.color) {
                        this.dictionary.colors[
                            Number(configuration.MuntinsData.color.id)
                                + '+'
                                + Number(configuration.MuntinsData.color.RAL)
                        ] = {
                            id: Number(configuration.MuntinsData.color.id),
                            idWin: Number(configuration.MuntinsData.color.idWin),
                            name: {
                                [this.langCode]: configuration.MuntinsData.color.name,
                            },
                            code: configuration.MuntinsData.color.code,
                            color: configuration.MuntinsData.color.color,
                            color_img: configuration.MuntinsData.color.color_img,
                            RAL: Boolean(configuration.MuntinsData.color.RAL),
                            type: configuration.MuntinsData.color.type,
                            visualization: configuration.MuntinsData.color.visualization,
                        };
                        this.muntins.colorInner = {
                            id: Number(configuration.MuntinsData.color.id),
                            isDefault: false,
                            RAL: Boolean(configuration.MuntinsData.color.RAL),
                        };
                    }

                    if (configuration.MuntinsData.colorOut) {
                        this.dictionary.colors[
                            Number(configuration.MuntinsData.colorOut.id)
                                + '+'
                                + Number(configuration.MuntinsData.colorOut.RAL)
                        ] = {
                            id: Number(configuration.MuntinsData.colorOut.id),
                            idWin: Number(configuration.MuntinsData.colorOut.idWin),
                            name: {
                                [this.langCode]: configuration.MuntinsData.colorOut.name,
                            },
                            code: configuration.MuntinsData.colorOut.code,
                            color: configuration.MuntinsData.colorOut.color,
                            color_img: configuration.MuntinsData.colorOut.color_img,
                            RAL: Boolean(configuration.MuntinsData.colorOut.RAL),
                            type: configuration.MuntinsData.colorOut.type,
                            visualization: configuration.MuntinsData.colorOut.visualization,
                        };
                        this.muntins.colorOuter = {
                            id: Number(configuration.MuntinsData.colorOut.id),
                            isDefault: false,
                            RAL: Boolean(configuration.MuntinsData.colorOut.RAL),
                        };
                    }
                }
                this.dictionary.fittings[Number(configuration.Fitting.id)] = {
                    id: Number(configuration.Fitting.id),
                    name: {
                        [this.langCode]: configuration.Fitting.name,
                    },
                };
                this.fitting = Number(configuration.Fitting.id);

                this.accessories = configuration.Accessories.map<Accessory>(
                    accessory => new Accessory(accessory)
                );
                this.sideAccessories = {
                    top: configuration.SideAccessories.top.map<Accessory>(
                        accessory => new Accessory(accessory)
                    ),
                    bottom: configuration.SideAccessories.bottom.map<Accessory>(
                        accessory => new Accessory(accessory)
                    ),
                    left: configuration.SideAccessories.left.map<Accessory>(
                        accessory => new Accessory(accessory)
                    ),
                    right: configuration.SideAccessories.right.map<Accessory>(
                        accessory => new Accessory(accessory)
                    ),
                };

                this.doorPortal = configuration.doorPortal;

                this.rollerShutter = configuration.hasRoller
                    ? new RollerShutter(configuration.RollerShutter)
                    : null;
                this.mosquito = configuration.mosquito
                    ? new MosquitoConfiguration(configuration.mosquito)
                    : null;
                this.isMistakeProduct = configuration.IsMistakeProduct;
                this.hasWarranty = configuration.Warranty;
                this.frames = configuration.Frames.map(frame => ({
                    ...frame,
                    frame: frame.frame.map(f => {
                        if (f.reinforcement) {
                            this.dictionary.reinforcements[Number(f.reinforcement.id)] = {
                                id: Number(f.reinforcement.id),
                                name: {
                                    [this.langCode]: f.reinforcement.name,
                                },
                            };
                        }
                        return {
                            ...f,
                            reinforcement: f.reinforcement && f.reinforcement.id,
                        };
                    }),
                }));
                this.couplings = configuration.couplings.map(c => {
                    if (c.reinforcement) {
                        this.dictionary.reinforcements[Number(c.reinforcement.id)] = {
                            id: Number(c.reinforcement.id),
                            name: {
                                [this.langCode]: c.reinforcement.name,
                            },
                        };
                    }
                    return {
                        ...c,
                        dimensions: {
                            height: 0,
                            width: 0,
                        },
                        reinforcement: c.reinforcement && c.reinforcement.id,
                    };
                });
                this.parameters = {
                    weight: configuration.Weight,
                };
                this.weldFinishType = configuration.weldFinishType;
                this.drainagePosition = configuration.drainagePosition;
                this.hasGlazingWarranty = configuration.GlazingWarranty;
                this.measurements = configuration.Measurements;
                this.glazingBeadType = configuration.GlazingBeadType;
                this.doorSizes = configuration.doorSizes;
                Object.keys(configuration.doorSizesDictionary.door).forEach(id => {
                    const size = configuration.doorSizesDictionary.door[id];
                    this.dictionary.doorSizes[id] = {
                        id: size.id,
                        name: {
                            [this.langCode]: size.name,
                        },
                        passive: size.passive
                    };
                });
                Object.keys(configuration.doorSizesDictionary.doorLights).forEach(id => {
                    const size = configuration.doorSizesDictionary.doorLights[id];
                    this.dictionary.doorLightSizes[id] = {
                        id: size.id,
                        name: {
                            [this.langCode]: size.name,
                        },
                        type: size.type
                    };
                });

                if (configuration.thresholdColor && configuration.thresholdColor.id) {
                    this.dictionary.colors[
                        Number(configuration.thresholdColor.id)
                            + '+'
                            + Number(configuration.thresholdColor.RAL)
                    ] = {
                        id: Number(configuration.thresholdColor.id),
                        name: {
                            [this.langCode]: configuration.thresholdColor.name,
                        },
                        code: configuration.thresholdColor.code,
                        color: configuration.thresholdColor.color,
                        color_img: configuration.thresholdColor.color_img,
                        RAL: Boolean(configuration.thresholdColor.RAL),
                        type: configuration.thresholdColor.type,
                        groups: (configuration.thresholdColor.groups
                            && configuration.thresholdColor.groups.map(Number))
                            || [],
                        visualization: configuration.thresholdColor.visualization,
                    };
                    this.thresholdColor = {
                        id: Number(configuration.thresholdColor.id),
                        RAL: Boolean(configuration.thresholdColor.RAL),
                        isDefault: configuration.thresholdColor.isDefault,
                    };
                }
                if (configuration.lippingColor && configuration.lippingColor.id) {
                    this.dictionary.colors[
                        Number(configuration.lippingColor.id)
                            + '+'
                            + Number(configuration.lippingColor.RAL)
                    ] = {
                        id: Number(configuration.lippingColor.id),
                        name: {
                            [this.langCode]: configuration.lippingColor.name,
                        },
                        code: configuration.lippingColor.code,
                        color: configuration.lippingColor.color,
                        color_img: configuration.lippingColor.color_img,
                        RAL: Boolean(configuration.lippingColor.RAL),
                        type: configuration.lippingColor.type,
                        groups:
                            (configuration.lippingColor.groups
                                && configuration.lippingColor.groups.map(Number))
                            || [],
                        visualization: configuration.lippingColor.visualization
                    };
                    this.lippingColor = {
                        id: Number(configuration.lippingColor.id),
                        RAL: Boolean(configuration.lippingColor.RAL),
                        isDefault: configuration.lippingColor.isDefault,
                    };
                }

                if (configuration.innerLippingColor && configuration.innerLippingColor.id) {
                    this.dictionary.colors[
                        Number(configuration.innerLippingColor.id)
                            + '+'
                            + Number(configuration.innerLippingColor.RAL)
                    ] = {
                        id: Number(configuration.innerLippingColor.id),
                        name: {
                            [this.langCode]: configuration.innerLippingColor.name,
                        },
                        code: configuration.innerLippingColor.code,
                        color: configuration.innerLippingColor.color,
                        color_img: configuration.innerLippingColor.color_img,
                        RAL: Boolean(configuration.innerLippingColor.RAL),
                        type: configuration.innerLippingColor.type,
                        groups:
                            (configuration.innerLippingColor.groups
                                && configuration.innerLippingColor.groups.map(Number))
                            || [],
                        visualization: configuration.innerLippingColor.visualization
                    };
                    this.innerLippingColor = {
                        id: Number(configuration.innerLippingColor.id),
                        RAL: Boolean(configuration.innerLippingColor.RAL),
                        isDefault: configuration.innerLippingColor.isDefault,
                    };
                }
            } else if (WindowSmallConfiguration.is(configuration)) {
                this.price = configuration.price;
                this.quantity = configuration.quantity;
                this.title = configuration.title;
                this.height = configuration.height;
                this.width = configuration.width;

                (['frame', 'sash'] as const).forEach(part => {
                    (['outer', 'inner', 'core', 'alushell'] as const).forEach(side => {
                        if (
                            (configuration.colors[part][side]
                                && configuration.colors[part][side].id)
                            || side === 'core'
                        ) {
                            let color;
                            if (side === 'core') {
                                color = configuratorsDataService.get('windowColorsAll', {
                                    key: 'type',
                                    val: 'white',
                                });
                            } else {
                                color = configuratorsDataService.get(
                                    'windowColorsAll',
                                    configuration.colors[part][side].id
                                );
                            }
                            this.dictionary.colors[
                                Number(color.id)
                                    + '+'
                                    + Number(configuration.colors[part][side].RAL)
                            ] = {
                                id: Number(color.id),
                                name: color.name,
                                code: color.code,
                                color: color.color,
                                type: color.type,
                                color_img: color.color_img,
                                RAL: Boolean(configuration.colors[part][side].RAL),
                                visualization: color.visualization,
                            };
                            this.colors[part][side] = {
                                id: Number(color.id),
                                RAL: Boolean(configuration.colors[part][side].RAL),
                                isDefault: Boolean(configuration.colors[part][side].isDefault),
                            };
                        }
                    });
                });

                this.colorType = this.getColorType(this.colors) as ColorType;
                this.colorsSashExt = false;
                if (configuration.wood && configuration.wood.id) {
                    const wood = configuratorsDataService.get('woodTypes', configuration.wood.id);
                    this.dictionary.woods[Number(wood.id)] = {
                        id: Number(wood.id),
                        name: {
                            [this.langCode]: wood.name,
                        },
                    };
                    this.wood = Number(wood.id);
                }
                this.hasAlushell = configuration.hasAlushell;

                this.shape = {
                    shape: configuration.shape.shape,
                    circuit: (configuration.shape.h1 + configuration.shape.h3) * 2,
                };
                ['width', 'height', 's1', 's2', 's3', 'h1', 'h2', 'h3', 'd'].forEach(field => {
                    if (configuration.shape[field] != null) {
                        this.shape[field] = Number(configuration.shape[field]);
                    }
                });
                if (configuration.shape.type && configuration.shape.type != null) {
                    this.shape.type = configuration.shape.type;
                }
                const sashesData = this.getSashesData(configuration.sashes);
                this.sashesType = sashesData.sashesTypes;
                this.ownedSashesTypes = {
                    window: sashesData.ownedSashesTypes.window,
                    doorActive: sashesData.ownedSashesTypes.doorActive,
                    doorPassive: sashesData.ownedSashesTypes.doorPassive,
                    doorTopLight: sashesData.ownedSashesTypes.doorTopLight,
                    doorLeftLight: sashesData.ownedSashesTypes.doorLeftLight,
                    doorRightLight: sashesData.ownedSashesTypes.doorRightLight,
                };
                this.sideProfiles = [];
                this.alignments = [];
                this.mullions = configuration.mullions.map<MullionV2>(
                    this.mapMullionSmallConf.bind(this)
                );

                this.oneGlazing = sashesData.oneGlazing;

                this.oneHandle = sashesData.oneHandle;

                const glazingBeadColor = this.colors.sash.inner.id
                    ? this.colors.sash.inner
                    : this.colors.sash.core;
                const color = this.dictionary.colors[
                    glazingBeadColor.id + '+' + Number(glazingBeadColor.RAL)
                ];
                this.dictionary.glazingBeadColors[glazingBeadColor.id] = {
                    id: glazingBeadColor.id,
                    name: color.name,
                    RAL: glazingBeadColor.RAL,
                };
                this.glazingBeadColor = {
                    id: glazingBeadColor.id,
                    isDefault: glazingBeadColor.isDefault,
                    RAL: glazingBeadColor.RAL,
                };
                if (configuration.sealColor && configuration.sealColor.id) {
                    const sealColor = configuratorsDataService.get(
                        'windowSealColors',
                        configuration.sealColor.id
                    );
                    this.dictionary.sealColors[Number(sealColor.id)] = {
                        id: Number(sealColor.id),
                        name: {
                            [this.langCode]: sealColor.name,
                        },
                    };
                    this.sealColor = {
                        id: Number(sealColor.id),
                        isDefault: configuration.sealColor.isDefault,
                    };
                }
                if (configuration.siliconeColor && configuration.siliconeColor.id) {
                    const siliconeColor = configuratorsDataService.get(
                        'windowSiliconeColors',
                        configuration.siliconeColor.id
                    );
                    this.dictionary.siliconeColors[Number(siliconeColor.id)] = {
                        id: Number(siliconeColor.id),
                        name: {
                            [this.langCode]: siliconeColor.name,
                        },
                    };
                    this.siliconeColor = {
                        id: Number(siliconeColor.id),
                        isDefault: configuration.siliconeColor.isDefault,
                    };
                }
                this.hasHandle = sashesData.hasHandle;
                this.handleType = sashesData.handleType as HandlesType;
                if (configuration.glazingSpacer && configuration.glazingSpacer.id) {
                    const glazingSpacer = configuratorsDataService.get(
                        'windowWarmEdges',
                        configuration.glazingSpacer.id
                    );
                    if (glazingSpacer && glazingSpacer.id) {
                        this.dictionary.glazingSpacers[Number(glazingSpacer.id)] = {
                            id: Number(glazingSpacer.id),
                            name: {
                                [this.langCode]: glazingSpacer.name,
                            },
                        };
                        this.glazingSpacer = Number(glazingSpacer.id);
                    }
                }
                if (
                    configuration.sashes.some(
                        sash =>
                            (sash.muntins && sash.muntins.length > 0)
                            || (sash.intSashes
                                && sash.intSashes.some(
                                    intSash => intSash.muntins && intSash.muntins.length > 0
                                ))
                    )
                ) {
                    const muntinType = configuratorsDataService.get(
                        'windowBarTypes',
                        configuration.muntins.type.id
                    );
                    let sizeId = muntinType.sizes.find(
                        el => el.size === configuration.muntins.size
                    );
                    sizeId = sizeId ? Number(sizeId.id) : null;
                    const colorInner = configuratorsDataService.get(
                        'windowColorsAll',
                        configuration.muntins.colorInner.id
                    );
                    const colorOuter =
                        configuration.muntins.colorInner.id === configuration.muntins.colorOuter.id
                            ? core.copy(colorInner)
                            : configuratorsDataService.get(
                                  'windowColorsAll',
                                  configuration.muntins.colorOuter.id
                              );
                    this.dictionary.colors[
                        Number(colorInner.id) + '+' + Number(configuration.muntins.colorInner.RAL)
                    ] = {
                        id: Number(colorInner.id),
                        idWin: Number(colorInner.idWin),
                        name: {
                            [this.langCode]: colorInner.name,
                        },
                        code: colorInner.code,
                        color: colorInner.color,
                        color_img: colorInner.color_img,
                        RAL: Boolean(configuration.muntins.colorInner.RAL),
                        type: colorInner.type,
                        visualization: colorInner.visualization,
                    };
                    this.dictionary.colors[
                        Number(colorOuter.id) + '+' + Number(configuration.muntins.colorOuter.RAL)
                    ] = {
                        id: Number(colorOuter.id),
                        idWin: Number(colorOuter.idWin),
                        name: {
                            [this.langCode]: colorOuter.name,
                        },
                        code: colorOuter.code,
                        color: colorOuter.color,
                        color_img: colorOuter.color_img,
                        RAL: Boolean(configuration.muntins.colorOuter.RAL),
                        type: colorOuter.type,
                        visualization: colorOuter.visualization,
                    };
                    this.muntins = {
                        sizeId,
                        size: Number(configuration.muntins.size),
                        colorDraw: '#' + colorInner.color.toUpperCase(),
                        colorInner: {
                            id: Number(colorInner.id),
                            isDefault: Boolean(configuration.muntins.colorInner.isDefault),
                            RAL: Boolean(configuration.muntins.colorInner.RAL),
                        },
                        colorOuter: {
                            id: Number(colorOuter.id),
                            isDefault: Boolean(configuration.muntins.colorOuter.isDefault),
                            RAL: Boolean(configuration.muntins.colorOuter.RAL),
                        },
                        type: {
                            id: Number(muntinType.id),
                            type: muntinType.type,
                        },
                        duplex: configuration.muntins.duplex,
                    };
                }
                this.sashes = configuration.sashes.map<SashV2>(this.mapSashSmallConf.bind(this));
                const systemsIds = configuration.system.id.split('|');
                if (systemsIds.length === 1) {
                    systemsIds[1] = systemsIds[0];
                }
                const filling =
                    core.copy(
                        this.configuratorsDataService.data.fillings.find(
                            e => String(e.id) === String(configuration.sashes[0].filling.id)
                        )
                    )
                    || core.copy(
                        this.configuratorsDataService.data.fillings.find(
                            e =>
                                String(e.id.split('.')[0])
                                === String(configuration.sashes[0].filling.id)
                        )
                    );
                const system = configuratorsDataService.get(
                    'windowLinesAll',
                    Number(systemsIds[Number(filling.glazing_count) - 2])
                );
                this.name = (system.type ? system.type.toUpperCase() + ' > ' : '') + system.name;
                this.dictionary.systems[Number(system.id)] = {
                    id: Number(system.id),
                    imageSideOnOffer: system.image_side_on_offer,
                    name: {
                        [this.langCode]: system.name,
                    },
                    titleImage: system.title_image,
                    type: system.type,
                    offerInfo: system.offer_info,
                    frameType: system.type_frame,
                    confType: system.confType,
                    doorType: system.door_type,
                    frameJointAngles: system.frame_joint_angles,
                    sashJointAngles: system.sash_joint_angles,
                    thresholdJointAngles: system.threshold_joint_angles,
                    glazingBeadJointAngles: system.glazing_bead_joint_angles,
                    windowLineGroupId: system.window_line_group_id
                };
                this.system = Number(system.id);
                this.systemPackets = [];
                let fitting = configuratorsDataService.get(
                    'windowFittings',
                    configuration.fitting.id
                );
                if (!fitting) {
                    fitting = configuratorsDataService.data.windowFittings.find(
                        el =>
                            el.window_lines_ids.includes(system.id + '')
                            && el.system_types.includes(system.type)
                    );
                }
                this.dictionary.fittings[Number(fitting.id)] = {
                    id: Number(fitting.id),
                    name: {
                        [this.langCode]: fitting.name,
                    },
                };
                this.fitting = Number(fitting.id);

                this.accessories = configuration.accessories
                    .map<Accessory>(accessory => new Accessory(accessory, configuratorsDataService))
                    .filter(el => el && el.id);
                this.sideAccessories = {
                    top: configuration.sideAccessories.top
                        .map<Accessory>(
                            accessory => new Accessory(accessory, configuratorsDataService)
                        )
                        .filter(el => el && el.id),
                    bottom: configuration.sideAccessories.bottom
                        .map<Accessory>(
                            accessory => new Accessory(accessory, configuratorsDataService)
                        )
                        .filter(el => el && el.id),
                    left: configuration.sideAccessories.left
                        .map<Accessory>(
                            accessory => new Accessory(accessory, configuratorsDataService)
                        )
                        .filter(el => el && el.id),
                    right: configuration.sideAccessories.right
                        .map<Accessory>(
                            accessory => new Accessory(accessory, configuratorsDataService)
                        )
                        .filter(el => el && el.id),
                };
                const newFrame = new FrameV2({
                    id: 0,
                    index: 1,
                    x: 0,
                    y: 0,
                    height: configuration.height,
                    width: configuration.width,
                });
                newFrame.frame = [];
                newFrame.lowThreshold = false;
                newFrame.shape = configuration.shape.shape;
                newFrame.parameters = {
                    ap: null,
                    g: null,
                    lt: null,
                    rw: null,
                    ug: null,
                    uw: null,
                    sw: null,
                    weight: null,
                    wl: null,
                    wp: null,
                    lab: null,
                };
                this.frames = [newFrame];
            }
        }
        delete this.configuratorsDataService;
        delete this.colorsDefaultsService;
    }

    public static is(configuration): configuration is WindowConfiguration {
        return (
            configuration
            && (configuration instanceof WindowConfiguration
                || (configuration.$version
                    && !configuration.active
                    && !(configuration instanceof WindowSmallConfiguration)
                    && ['window', 'door', 'hs', 'folding_door', 'sliding_door'].indexOf(
                        configuration.type
                    ) > -1))
        );
    }

    protected runConversion(configuration, dataRequiredToUpdate: any) {
        let updatedConfiguration = core.copy(configuration);

        for (let version = configuration.$version + 1; version <= this.$version; version++) {
            updatedConfiguration.$version = version;
            if (WindowConfiguration.converters[version] && typeof WindowConfiguration.converters[version] === 'function') {
                updatedConfiguration = WindowConfiguration.converters[version](
                    updatedConfiguration,
                    dataRequiredToUpdate
                );
            }
        }

        (Object as any)
            .entries(updatedConfiguration)
            .forEach(([key, value]) => {
                this[key] = value;
            });
    }

    private mapSash(
        sash: ActiveSash,
        parentSash: ActiveSash,
        configuration: WindowActiveConfiguration
    ): SashV2 {
        this.dictionary.fillings[sash.glazing.custom ? sash.glazing.id : sash.glazing.id] = {
            id: sash.glazing.custom ? sash.glazing.id : sash.glazing.id,
            extId: sash.glazing.extId,
            custom: sash.glazing.custom || false,
            name: {
                [this.langCode]: sash.glazing.name,
            },
            producer: sash.glazing.producer,
            type: sash.glazing.type,
            weight: Number(sash.glazing.weight),
            availableSecondColor: sash.glazing.available_second_color,
            overlay_type: sash.glazing.panel_type,
            default_colors: sash.glazing.default_colors,
            thickness_mm: String(sash.glazing.thickness_mm),
            overlay_door_viewer_position_x_mm: Number(
                sash.glazing.overlay_door_viewer_position_x_mm
            ),
            overlay_door_viewer_position_y_mm: Number(
                sash.glazing.overlay_door_viewer_position_y_mm
            ),
            texture: sash.glazing.texture,
            inside_texture: sash.glazing.inside_texture,
            fillingColor: sash.glazing.color,
            indexNo: sash.glazing.index_no,
            security_level_inner: sash.glazing.security_level_inner,
            security_level_outer: sash.glazing.security_level_outer,
            glass_types: sash.glazing.glass_types,
            u: sash.glazing.u
        };

        (['selectedColor', 'selectedColorSecond'] as const).forEach(colorType => {
            if (sash.glazing[colorType]) {
                (['outer', 'inner', 'core'] as const).forEach(side => {
                    if (sash.glazing[colorType].frame[side]?.id) {
                        const colorId =
                            Number(sash.glazing[colorType].frame[side].id)
                            + '+'
                            + Number(sash.glazing[colorType].frame[side].RAL);
                        if (!this.dictionary.colors[colorId]) {
                            this.dictionary.colors[colorId] = {
                                id: Number(sash.glazing[colorType].frame[side].id),
                                name: {
                                    [this.langCode]: sash.glazing[colorType].frame[side].name,
                                },
                                code: sash.glazing[colorType].frame[side].code,
                                color: sash.glazing[colorType].frame[side].color,
                                color_img: sash.glazing[colorType].frame[side].color_img,
                                RAL: Boolean(sash.glazing[colorType].frame[side].RAL),
                                type: sash.glazing[colorType].frame[side].type,
                                groups:
                                    (sash.glazing[colorType].frame[side].groups
                                        && sash.glazing[colorType].frame[side].groups.map(Number))
                                    || [],
                                visualization: sash.glazing[colorType].frame[side].visualization,
                            };
                        }
                    }
                });
            }
        });

        const newSash: SashV2 = {
            id: Number(sash.id),
            index: Number(sash.index),
            frameId: Number(sash.frameId),
            x: Number(sash.rx),
            y: Number(sash.ry),
            width: Number(sash.rWidth),
            height: Number(sash.rHeight),
            muntins: sash.muntins,
            nearMullions: {
                left: Number(sash.nearMullions.left),
                right: Number(sash.nearMullions.right),
                top: Number(sash.nearMullions.top),
                bottom: Number(sash.nearMullions.bottom),
            },
            nearAlignments: {
                left: Number(sash.nearAlignments.left),
                right: Number(sash.nearAlignments.right),
                top: Number(sash.nearAlignments.top),
                bottom: Number(sash.nearAlignments.bottom),
            },
            filling: {
                id: sash.glazing.id,
                type: sash.glazing.type,
                color: !sash.glazing.selectedColor
                    ? null
                    : {
                          inner: iccColorToColorId(sash.glazing.selectedColor.frame.inner),
                          outer: iccColorToColorId(sash.glazing.selectedColor.frame.outer),
                          core: iccColorToColorId(sash.glazing.selectedColor.frame.core),
                      },
                visualization:
                    (sash.glazing.visualizations && sash.glazing.visualizations[-1]) || null,
                default_colors: sash.glazing.default_colors,
                overlay_type: sash.glazing.panel_type,
                thickness_mm: sash.glazing.thickness_mm,
                overlayColor: !sash.glazing.selectedColorSecond
                    ? null
                    : {
                          inner: iccColorToColorId(sash.glazing.selectedColorSecond.frame.inner),
                          outer: iccColorToColorId(sash.glazing.selectedColorSecond.frame.outer),
                          core: iccColorToColorId(sash.glazing.selectedColorSecond.frame.core),
                      },
            },
            glazingBead: sash.glazingBead as GlazingBeadProfile,
            bondedGlazing: Boolean(sash.bondedGlazing),
            panelGlazing: null,
            panelInner: null,
            fillingSizes: sash.glazingSizes
                ? {
                      area: Number(sash.glazingSizes.area),
                      width: Number(sash.glazingSizes.width),
                      height: Number(sash.glazingSizes.height),
                      x: Number(sash.glazingSizes.x),
                      y: Number(sash.glazingSizes.y),
                  }
                : null,
            shape: sash.shape.shape,
            hardware: sash.hardware.map(accessory => new Accessory(accessory)),
            mosquito: sash.mosquito ? new MosquitoConfiguration(sash.mosquito) : null,
            weight: sash.weight,
        };

        ['u', 'rw', 'c', 'ctr', 'lt', 'g', 'psi'].forEach(field => {
            if (sash.glazing[field] != null) {
                newSash.filling[field] = Number(sash.glazing[field]);
            }
        });

        let doorSizeId: number | null = null;
        if (configuration.doorSizes) {
            if (parentSash.type) {
                if (parentSash.type.type === 'DRA' || parentSash.type.type === 'DOA') {
                    doorSizeId = configuration.doorSizes.sashSizeId;
                } else if (parentSash.type.type === 'DRP' || parentSash.type.type === 'DOP') {
                    doorSizeId = configuration.doorSizes.passiveSashSizeId;
                }
            }
            if (
                doorSizeId
                && sash.glazing.visualizations
                && sash.glazing.visualizations[doorSizeId]
            ) {
                newSash.filling.visualization = sash.glazing.visualizations[doorSizeId];
            }
        }

        
        if (sash.glazing.type === 'deco_panels' || sash.glazing.type === 'door_panels') {
            if (sash.panelGlazing && sash.panelGlazing.id) {
                this.dictionary.fillings[Number(sash.panelGlazing.id)] = {
                    id: Number(sash.panelGlazing.id),
                    extId: 0,
                    custom: sash.panelGlazing.custom || false,
                    name: {
                        [this.langCode]: sash.panelGlazing.name,
                    },
                    producer: sash.panelGlazing.producer,
                    type: sash.panelGlazing.type,
                    weight: Number(sash.panelGlazing.weight),
                    availableSecondColor: sash.panelGlazing.available_second_color,
                    texture: sash.panelGlazing.texture,
                    inside_texture: sash.panelGlazing.inside_texture,
                    fillingColor: sash.panelGlazing.color,
                    indexNo: sash.panelGlazing.index_no,
                };
                newSash.panelGlazing = {
                    id: Number(sash.panelGlazing.id),
                    color: null,
                    overlayColor: null,
                    custom: false,
                    visualization: null,
                };
                ['u', 'rw', 'c', 'ctr', 'lt', 'g', 'psi'].forEach(field => {
                    if (sash.panelGlazing[field] != null) {
                        newSash.panelGlazing[field] = Number(sash.panelGlazing[field]);
                    }
                });
                if (sash.panelGlazing.visualizations) {
                    const visualizationsForFilling =
                        sash.panelGlazing.visualizations[parseInt(sash.glazing.id)]
                        || sash.panelGlazing.visualizations[-1];
                    if (visualizationsForFilling) {
                        const visualization =
                            (doorSizeId && visualizationsForFilling[doorSizeId])
                            || visualizationsForFilling[-1];
                        if (visualization) {
                            newSash.panelGlazing.visualization = visualization;
                        }
                    }
                }
            }
            newSash.panelType = sash.panelType;
            if (sash.panelInner && sash.panelInner.id) {
                this.dictionary.fillings[Number(sash.panelInner.id)] = {
                    id: Number(sash.panelInner.id),
                    extId: 0,
                    custom: false,
                    name: {
                        [this.langCode]: sash.panelInner.name,
                    },
                    producer: sash.panelInner.producer,
                    type: sash.panelInner.type,
                    weight: Number(sash.panelInner.weight),
                    availableSecondColor: sash.panelInner.available_second_inside_color,
                    texture: sash.panelInner.texture,
                    inside_texture: sash.panelInner.inside_texture,
                    fillingColor: sash.panelInner.color,
                    indexNo: sash.panelInner.index_no,
                };
                (['selectedColor', 'selectedColorSecond'] as const).forEach(colorType => {
                    if (sash.panelInner[colorType]) {
                        (['outer', 'inner', 'core'] as const).forEach(side => {
                            if (sash.panelInner[colorType].frame[side]?.id) {
                                const colorId =
                                    Number(sash.panelInner[colorType].frame[side].id)
                                    + '+'
                                    + Number(sash.panelInner[colorType].frame[side].RAL);
                                if (!this.dictionary.colors[colorId]) {
                                    this.dictionary.colors[colorId] = {
                                        id: Number(sash.panelInner[colorType].frame[side].id),
                                        name: {
                                            [this.langCode]:
                                                sash.panelInner[colorType].frame[side].name,
                                        },
                                        code: sash.panelInner[colorType].frame[side].code,
                                        color: sash.panelInner[colorType].frame[side].color,
                                        color_img:
                                            sash.panelInner[colorType].frame[side].color_img,
                                        RAL: Boolean(
                                            sash.panelInner[colorType].frame[side].RAL
                                        ),
                                        type: sash.panelInner[colorType].frame[side].type,
                                        visualization: sash.panelInner[colorType].frame[side].visualization,
                                        groups:
                                            (sash.panelInner[colorType].frame[side].groups
                                                && sash.panelInner[colorType].frame[
                                                    side
                                                ].groups.map(Number))
                                            || [],
                                    };
                                }
                            }
                        });
                    }
                });
                newSash.panelInner = {
                    id: Number(sash.panelInner.id),
                    type: sash.panelInner.type,
                    color: !sash.panelInner.selectedColor
                        ? null
                        : {
                              inner: iccColorToColorId(
                                  sash.panelInner.selectedColor.frame.inner
                              ),
                              outer: iccColorToColorId(
                                  sash.panelInner.selectedColor.frame.outer
                              ),
                              core: iccColorToColorId(sash.panelInner.selectedColor.frame.core),
                          },
                    visualization:
                        (sash.panelInner.visualizations && sash.panelInner.visualizations[-1])
                        || null,
                    default_colors: sash.panelInner.default_colors,
                    overlay_type: sash.panelInner.panel_type,
                    thickness_mm: sash.panelInner.thickness_mm,
                    overlayColor: !sash.panelInner.selectedColorSecond
                        ? null
                        : {
                              inner: iccColorToColorId(
                                  sash.panelInner.selectedColorSecond.frame.inner
                              ),
                              outer: iccColorToColorId(
                                  sash.panelInner.selectedColorSecond.frame.outer
                              ),
                              core: iccColorToColorId(
                                  sash.panelInner.selectedColorSecond.frame.core
                              ),
                          },
                };
                if (
                    doorSizeId
                    && sash.panelInner.visualizations
                    && sash.panelInner.visualizations[doorSizeId]
                ) {
                    newSash.panelInner.visualization =
                        sash.panelInner.visualizations[doorSizeId];
                }
            }
        }

        if (sash.parentId != null) {
            newSash.parentId = sash.parentId;
        } else {
            this.dictionary.sashTypes[Number(sash.type.id)] = {
                id: Number(sash.type.id),
                type: sash.type.type,
                name: {
                    [this.langCode]: sash.type.type,
                },
                handlePosition: sash.type.handle_position,
                passive: sash.type.passive,
            };
            newSash.type = {
                id: Number(sash.type.id),
                handlePosition: sash.type.handle_position,
                passive: sash.type.passive
            };
            newSash.intSashes = sash.intSashes.map(field =>
                this.mapSash(field, sash, configuration)
            );
            newSash.intAlignments = sash.intAlignments;
            newSash.intMullions = sash.intMullions.map(this.mapMullion.bind(this));
            newSash.intAdjacentSashes = {
                top: sash.intEdgeSashes.top
                    .filter(e => e !== null && e !== undefined)
                    .map(edgeSash => Number(edgeSash)),
                bottom: sash.intEdgeSashes.bottom
                    .filter(e => e !== null && e !== undefined)
                    .map(edgeSash => Number(edgeSash)),
                left: sash.intEdgeSashes.left
                    .filter(e => e !== null && e !== undefined)
                    .map(edgeSash => Number(edgeSash)),
                right: sash.intEdgeSashes.right
                    .filter(e => e !== null && e !== undefined)
                    .map(edgeSash => Number(edgeSash)),
            };

            newSash.oneGlazing = sash.oneGlazing;
            newSash.oneGlazingBead = sash.oneGlazingBead;

            newSash.frame = {
                top: {
                    ...sash.frame.top,
                    reinforcement: sash.frame.top.reinforcement && sash.frame.top.reinforcement.id,
                },
                bottom: {
                    ...sash.frame.bottom,
                    reinforcement:
                        sash.frame.bottom.reinforcement && sash.frame.bottom.reinforcement.id,
                },
                left: {
                    ...sash.frame.left,
                    reinforcement:
                        sash.frame.left.reinforcement && sash.frame.left.reinforcement.id,
                },
                right: {
                    ...sash.frame.right,
                    reinforcement:
                        sash.frame.right.reinforcement && sash.frame.right.reinforcement.id,
                },
            };
            if (sash.frame.top.reinforcement) {
                this.dictionary.reinforcements[Number(sash.frame.top.reinforcement.id)] = {
                    id: Number(sash.frame.top.reinforcement.id),
                    name: {
                        [this.langCode]: sash.frame.top.reinforcement.name,
                    },
                };
            }
            if (sash.frame.bottom.reinforcement) {
                this.dictionary.reinforcements[Number(sash.frame.bottom.reinforcement.id)] = {
                    id: Number(sash.frame.bottom.reinforcement.id),
                    name: {
                        [this.langCode]: sash.frame.bottom.reinforcement.name,
                    },
                };
            }
            if (sash.frame.left.reinforcement) {
                this.dictionary.reinforcements[Number(sash.frame.left.reinforcement.id)] = {
                    id: Number(sash.frame.left.reinforcement.id),
                    name: {
                        [this.langCode]: sash.frame.left.reinforcement.name,
                    },
                };
            }
            if (sash.frame.right.reinforcement) {
                this.dictionary.reinforcements[Number(sash.frame.right.reinforcement.id)] = {
                    id: Number(sash.frame.right.reinforcement.id),
                    name: {
                        [this.langCode]: sash.frame.right.reinforcement.name,
                    },
                };
            }

            if (
                ['F', 'FF', 'OFF', 'DS', 'ODS', 'DSC', 'DRP', 'DOP'].indexOf(
                    this.dictionary.sashTypes[newSash.type.id].type
                ) === -1
            ) {
                if (sash.handle) {
                    this.dictionary.handles[Number(sash.handle.id)] = {
                        id: Number(sash.handle.id),
                        handleType: sash.handle.handle_type,
                        name: {
                            [this.langCode]: sash.handle.name,
                        },
                    };
                    if (sash.handleColor) {
                        this.dictionary.accessoryColors[Number(sash.handleColor.id)] = {
                            id: Number(sash.handleColor.id),
                            name: {
                                [this.langCode]: sash.handleColor.name,
                            },
                            color: sash.handleColor.color,
                            code: sash.handleColor.code,
                            color_img: sash.handleColor.color_img,
                        };
                    }
                    
                    newSash.handleInner = sash.handle
                        ? {
                            id: Number(sash.handle.id),
                            color: sash.handleColor
                                ? {
                                        id: Number(sash.handleColor.id),
                                        isDefault: sash.handleColor.isDefault,
                                    }
                                : null,
                            visualization:
                                sash.handle.visualizations
                                && ((sash.handleColor
                                    && sash.handle.visualizations[sash.handleColor.id])
                                    || sash.handle.visualizations[-1]),
                        }
                        : null;
                }
                if (sash.handleOuter) {
                    this.dictionary.handles[Number(sash.handleOuter.id)] = {
                        id: Number(sash.handleOuter.id),
                        handleType: sash.handleOuter.handle_type,
                        name: {
                            [this.langCode]: sash.handleOuter.name,
                        },
                    };
                    if (sash.handleOuterColor) {
                        this.dictionary.accessoryColors[Number(sash.handleOuterColor.id)] = {
                            id: Number(sash.handleOuterColor.id),
                            name: {
                                [this.langCode]: sash.handleOuterColor.name,
                            },
                            color: sash.handleOuterColor.color,
                            code: sash.handleOuterColor.code,
                            color_img: sash.handleOuterColor.color_img,
                        };
                    }
                    newSash.handleOuter = sash.handleOuter
                        ? {
                            id: Number(sash.handleOuter.id),
                            color: sash.handleOuterColor
                                ? {
                                        id: Number(sash.handleOuterColor.id),
                                        isDefault: sash.handleOuterColor.isDefault,
                                    }
                                : null,
                            visualization:
                                sash.handleOuter.visualizations
                                && ((sash.handleOuterColor
                                    && sash.handleOuter.visualizations[sash.handleOuterColor.id])
                                    || sash.handleOuter.visualizations[-1]),
                        }
                        : null;
                }
                newSash.handleHeight = Number(sash.rHandleY);
                newSash.defaultHandleHeight = sash.defaultHandleHeight;
                newSash.handleHeightType = sash.handleHeightType;
            }
        }

        return newSash;
    }

    private mapSashSmallConf(sash): SashV2 {
        const filling =
            core.copy(
                this.configuratorsDataService.data.fillings.find(e => e.id === sash.filling.id)
            )
            || core.copy(
                this.configuratorsDataService.data.fillings.find(
                    e => e.id.split('.')[0] === sash.filling.id
                )
            );
        this.dictionary.fillings[filling.id] = {
            id: filling.id,
            extId: filling.extId,
            custom: false,
            name: {
                [this.langCode]: filling.name,
            },
            producer: filling.producer,
            type: filling.type,
            weight: Number(filling.weight),
            color: null,
            overlay_color: null,
            default_colors: null,
            availableSecondColor: filling.available_second_color,
            indexNo: filling.index_no,
            security_level_inner: filling.security_level_inner,
            security_level_outer: filling.security_level_outer,
            glass_types: filling.glass_types,
        };
        const newSash: SashV2 = {
            id: Number(sash.id),
            index: Number(sash.index),
            frameId: 0,
            x: Number(sash.x),
            y: Number(sash.y),
            width: Number(sash.width),
            height: Number(sash.height),
            muntins: sash.muntins,
            nearMullions: {
                left: Number(sash.nearMullions.left),
                right: Number(sash.nearMullions.right),
                top: Number(sash.nearMullions.top),
                bottom: Number(sash.nearMullions.bottom),
            },
            nearAlignments: {
                left: -1,
                right: -1,
                top: -1,
                bottom: -1,
            },
            filling: {
                id: filling.id,
                color: null,
                overlayColor: null,
                visualization: null,
                overlay_type: null,
                availableSecondColor: filling.available_second_color,
            },
            glazingBead: null,
            panelGlazing: null,
            panelInner: null,
            fillingSizes: null,
            shape: this.shape.shape,
            hardware: sash.hardware.map(
                accessory => new Accessory(accessory, this.configuratorsDataService)
            ),
            bondedGlazing: null,
            weight: 0,
        };

        if (newSash.muntins && Common.isObject(sash.muntins) && !Common.isArray(newSash.muntins)) {
            muntinsConversion(newSash);
        }

        if (sash.parentId != null) {
            newSash.parentId = sash.parentId;
        } else {
            let sashType: IccSashType;
            if (sash.type.id) {
                sashType = this.configuratorsDataService.get('sashTypes', sash.type.id);
            } else {
                sashType = this.configuratorsDataService.data.sashTypes.find(
                    el =>
                        el.type === sash.type.type
                        && (!sash.type.handlePosition
                            || sash.type.handlePosition === el.handle_position)
                );
            }
            this.dictionary.sashTypes[Number(sashType.id)] = {
                id: Number(sashType.id),
                type: sashType.type,
                name: {
                    [this.langCode]: sashType.type,
                },
                handlePosition: sash.type.handlePosition,
                passive: sashType.passive,
            };
            newSash.type = {
                id: Number(sashType.id),
                handlePosition: sashType.handle_position,
                passive: sashType.passive
            };
            newSash.intSashes = sash.intSashes.map(this.mapSashSmallConf.bind(this));
            newSash.intAlignments = [];
            newSash.intMullions = sash.intMullions.map(this.mapMullion.bind(this));
            newSash.intAdjacentSashes = this.getAdjacentSashes(sash);

            newSash.oneGlazing = sash.oneGlazing;
            newSash.oneGlazingBead = sash.oneGlazingBead;

            newSash.mosquito = null;

            newSash.frame = sash.frame || null;

            if (
                ['F', 'FF', 'OFF', 'DS', 'DSC', 'DRP'].indexOf(
                    this.dictionary.sashTypes[newSash.type.id].type
                ) === -1
            ) {
                const handleInner = this.configuratorsDataService.get(
                    'windowHandlers',
                    sash.handleInner.id
                );
                let handleInnerColor;
                if (sash.handleInner.color.id > 0) {
                    handleInnerColor = this.configuratorsDataService.get(
                        'windowHandlesColors',
                        sash.handleInner.color.id
                    );
                } else {
                    handleInnerColor = this.configuratorsDataService.data.windowHandlesColors.find(
                        el => el.color === sash.handleInner.color.color
                    );
                }
                const handleOuter = sash.handleOuter
                    ? this.configuratorsDataService.get('windowHandlers', sash.handleOuter.id)
                    : handleInner;
                let handleOuterColor;
                if (sash.handleOuter && sash.handleOuter.color.id > 0) {
                    handleOuterColor = sash.handleOuter
                        ? this.configuratorsDataService.get(
                              'windowHandlesColors',
                              sash.handleOuter.color.id
                          )
                        : handleInnerColor;
                } else if (sash.handleOuter && sash.handleOuter.color.id < 0) {
                    handleOuterColor = this.configuratorsDataService.data.windowHandlesColors.find(
                        el => el.color === sash.handleOuter.color.color
                    );
                } else {
                    handleOuterColor = handleInnerColor;
                }
                this.dictionary.handles[Number(handleInner.id)] = {
                    id: Number(handleInner.id),
                    handleType: handleInner.handle_type,
                    name: {
                        [this.langCode]: handleInner.name,
                    },
                };
                this.dictionary.accessoryColors[Number(handleInnerColor.id)] = {
                    id: Number(handleInnerColor.id),
                    name: {
                        [this.langCode]: handleInnerColor.name,
                    },
                    color: handleInnerColor.color,
                    code: handleInnerColor.code,
                    color_img: handleInnerColor.color_img,
                };
                newSash.handleInner = {
                    id: Number(handleInner.id),
                    color: {
                        id: Number(handleInnerColor.id),
                        isDefault: sash.handleInner.color.isDefault,
                    },
                    visualization: handleInner.visualizations && (sash.handleInner.color && handleInner.visualizations[sash.handleInner.color.id] || handleInner.visualizations[-1]),
                };
                this.dictionary.handles[Number(handleOuter.id)] = {
                    id: Number(handleOuter.id),
                    handleType: handleOuter.handle_type,
                    name: {
                        [this.langCode]: handleOuter.name,
                    },
                };
                this.dictionary.accessoryColors[Number(handleOuterColor.id)] = {
                    id: Number(handleOuterColor.id),
                    name: {
                        [this.langCode]: handleOuterColor.name,
                    },
                    color: handleOuterColor.color,
                    code: handleOuterColor.code,
                    color_img: handleOuterColor.color_img,
                };
                newSash.handleOuter = {
                    id: Number(handleOuter.id),
                    color: {
                        id: Number(handleOuterColor.id),
                        isDefault: sash.handleOuter
                            ? sash.handleOuter.color.isDefault
                            : sash.handleInner.color.isDefault,
                    },
                    visualization: handleOuter.visualizations && (sash.handleOuter.color && handleOuter.visualizations[sash.handleOuter.color.id] || handleOuter.visualizations[-1]),
                };
            }
        }

        return newSash;
    }

    private getSashesData(sashes: WindowSmallConfiguration['sashes']) {
        let allFix = true;
        let allFixSash = true;
        let allFunc = true;
        let sashesTypes: SashesType = 'Func';
        const ownedSashesTypes = {
            doorActive: false,
            doorPassive: false,
            window: false,
            doorTopLight: false,
            doorLeftLight: false,
            doorRightLight: false,
        };
        let oneGlazing = true;
        let oneHandle = true;
        let hasHandle = false;
        let handleType = null;
        let fillingId;
        let handleId;

        sashes.forEach(sash => {
            let sashType: IccSashType;
            if (sash.type.id) {
                sashType = this.configuratorsDataService.get('sashTypes', sash.type.id);
            }
            if (sashType.type === 'F' || sashType.type === 'FF' || sashType.type === 'OFF') {
                allFunc = false;
                if (sashType.type === 'F') {
                    allFixSash = false;
                } else {
                    allFix = false;
                }
            } else {
                allFix = false;
                allFixSash = false;
            }

            if (sashType.type === 'DRA') {
                ownedSashesTypes.doorActive = true;
            } else if (sashType.type === 'DRP') {
                ownedSashesTypes.doorPassive = true;
            } else if (sashType.type !== 'F' && sashType.type !== 'FF' && sashType.type !== 'OFF') {
                ownedSashesTypes.window = true;
            }

            if (sash.handleInner && sash.handleInner.id) {
                if (!handleId) {
                    handleId = Number(sash.handleInner.id);
                    const handle = this.configuratorsDataService.get('windowAccessories', handleId);
                    handleType = handle.handle_type;
                }
                hasHandle = true;
                oneHandle = oneHandle && handleId === Number(sash.handleInner.id);
            }

            if (sash.intSashes && sash.intSashes.length > 1) {
                sash.intSashes.forEach(intSash => {
                    if (!fillingId) {
                        fillingId = Number(intSash.filling.id);
                    }
                    oneGlazing = oneGlazing && fillingId === Number(intSash.filling.id);
                });
            } else {
                if (!fillingId) {
                    fillingId = Number(sash.filling.id);
                }
                oneGlazing = oneGlazing && fillingId === Number(sash.filling.id);
            }
        });

        if (allFix) {
            sashesTypes = 'Fix';
        } else if (allFixSash) {
            sashesTypes = 'FixSash';
        } else if (allFunc) {
            sashesTypes = 'Func';
        } else {
            sashesTypes = 'Mix';
        }

        return {
            sashesTypes,
            ownedSashesTypes,
            oneGlazing,
            oneHandle,
            hasHandle,
            handleType,
        };
    }

    private getAdjacentSashes(sash: ActiveSash) {
        const adjacentSashes = {
            top: [],
            bottom: [],
            left: [],
            right: [],
        };

        sash.intSashes.forEach(intSash => {
            ['top', 'right', 'bottom', 'left'].forEach(side => {
                if (intSash.nearMullions[side] && intSash.nearMullions[side] === -1) {
                    adjacentSashes[side].push(intSash.id);
                }
            });
        });

        return adjacentSashes;
    }

    private getColorType(colors) {
        let colorType = 'White';
        const outer = this.colorsDefaultsService.getColorValue(colors, 'outer');
        const inner = this.colorsDefaultsService.getColorValue(colors, 'inner');
        // const core = this.colorsDefaultsService.getColorValue(colors, 'core');

        if (
            this.colorsDefaultsService.getDefaultColorType('White', 'outer') === outer
            && this.colorsDefaultsService.getDefaultColorType('White', 'inner') === inner
            // && this.colorsDefaultsService.getDefaultColorType('White', 'core') == core
        ) {
            colorType = 'White';
        } else if (
            this.colorsDefaultsService.getDefaultColorType('Cream', 'outer') === outer
            && this.colorsDefaultsService.getDefaultColorType('Cream', 'inner') === inner
            // && this.colorsDefaultsService.getDefaultColorType('Cream', 'core') == core
        ) {
            colorType = 'Cream';
        } else if (
            this.colorsDefaultsService.getDefaultColorType('Outer', 'outer') === outer
            && this.colorsDefaultsService.getDefaultColorType('Outer', 'inner') === inner
            // && this.colorsDefaultsService.getDefaultColorType('Outer', 'core') == core
        ) {
            colorType = 'Outer';
        } else if (
            this.colorsDefaultsService.getDefaultColorType('Inner', 'outer') === outer
            && this.colorsDefaultsService.getDefaultColorType('Inner', 'inner') === inner
            // && this.colorsDefaultsService.getDefaultColorType('Inner', 'core') == core
        ) {
            colorType = 'Inner';
        } else if (
            ((this.colorsDefaultsService.getDefaultColorType('Bilateral', 'outer') === outer
                && this.colorsDefaultsService.getDefaultColorType('Bilateral', 'inner') === inner)
                || outer === inner)
            && colors.frame.outer.id === colors.frame.inner.id
        ) {
            colorType = 'Bilateral';
        } else {
            colorType = 'Bicolor';
        }

        return colorType;
    }

    private mapMullion(mullion: ActiveMullion): MullionV2 {
        const newMullion: MullionV2 = {
            id: Number(mullion.id),
            isDefault: mullion.isDefault,
            frameId: mullion.frameId,
            profileId: Number(mullion.profileId),
            position: Number(mullion.direction === 'horizontal' ? mullion.ry : mullion.rx),
            length: Number(mullion.direction === 'horizontal' ? mullion.rWidth : mullion.rHeight),
            shift: Number(mullion.direction === 'horizontal' ? mullion.rx : mullion.ry),
            direction: mullion.direction,
            type: mullion.type,
            adjacentSashes: {},
            parallelAlignments: {
                top: [],
                bottom: [],
                left: [],
                right: [],
            },
            perpendicularAlignments: {
                top: [],
                bottom: [],
                left: [],
                right: [],
            },
            parallelMullions: {},
            perpendicularMullions: {},
            reinforcement: mullion.reinforcement && mullion.reinforcement.id,
        };
        if (mullion.reinforcement) {
            this.dictionary.reinforcements[Number(mullion.reinforcement.id)] = {
                id: Number(mullion.reinforcement.id),
                name: {
                    [this.langCode]: mullion.reinforcement.name,
                },
            };
        }
        if (newMullion.direction === 'horizontal') {
            newMullion.adjacentSashes = {
                top: mullion.multiAlignTop
                    .filter(e => Common.isObject(e))
                    .map(sash => Number(sash.id)),
                bottom: mullion.multiAlignBottom
                    .filter(e => Common.isObject(e))
                    .map(sash => Number(sash.id)),
            };
            newMullion.perpendicularMullions = {
                top: mullion.multiAlignTopDiv
                    .filter(e => Common.isObject(e))
                    .map(div => Number(div.id)),
                bottom: mullion.multiAlignBottomDiv
                    .filter(e => Common.isObject(e))
                    .map(div => Number(div.id)),
            };
            newMullion.parallelAlignments = {
                top: mullion.parallelAlignments.top || [],
                bottom: mullion.parallelAlignments.bottom || [],
            };
            newMullion.perpendicularAlignments = {
                top: mullion.perpendicularAlignments.top || [],
                bottom: mullion.perpendicularAlignments.bottom || [],
            };
        } else {
            newMullion.adjacentSashes = {
                left: mullion.multiAlignLeft
                    .filter(e => Common.isObject(e))
                    .map(sash => Number(sash.id)),
                right: mullion.multiAlignRight
                    .filter(e => Common.isObject(e))
                    .map(sash => Number(sash.id)),
            };
            newMullion.perpendicularMullions = {
                left: mullion.multiAlignLeftDiv
                    .filter(e => Common.isObject(e))
                    .map(div => Number(div.id)),
                right: mullion.multiAlignRightDiv
                    .filter(e => Common.isObject(e))
                    .map(div => Number(div.id)),
            };
            newMullion.parallelAlignments = {
                left: mullion.parallelAlignments.left || [],
                right: mullion.parallelAlignments.right || [],
            };
            newMullion.perpendicularAlignments = {
                left: mullion.perpendicularAlignments.left || [],
                right: mullion.perpendicularAlignments.right || [],
            };
        }
        return newMullion;
    }

    private mapMullionSmallConf(mullion): MullionV2 {
        const newMullion: MullionV2 = {
            id: Number(mullion.id),
            frameId: 0,
            isDefault: true,
            profileId: Number(mullion.dividerId),
            position: Number(mullion.position),
            length: Number(mullion.length),
            shift: 0,
            direction: mullion.direction,
            adjacentSashes: mullion.adjacentSashes || {},
            parallelAlignments: {},
            perpendicularAlignments: {},
            parallelMullions: {},
            perpendicularMullions: mullion.perpendicularMullions || {},
            reinforcement: null,
            type: null,
        };
        return newMullion;
    }
}

export type IWindowConfiguration = Pick<WindowConfiguration, keyof WindowConfiguration>;
